import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import theme from "../../theme";
import useButtonStyles from "../../styles/useButtonStyles";
import SearchMemberDropdown from "../ui/SearchMemberDropdown";
import Loader from "../ui/Loader";
import { useAppSelector } from "../../redux/hooks";
import { getFeaturePermissions } from "../../util/permissions";
import useGetMemberDetails from "../../Hooks/member/useGetMemberDetails";
import QRScanner from "../ui/QRScanner";
import backgroundImageToLogin from "../../assets/loginBG.png";
import BasicModal from "../ui/BasicModal";
import { debounce } from "../../util/debounce";
import ScannerModal from "./ScannerModal";
import ScannerModalFail from "./ScannerModalFail";
import axios from "axios";
import { APIConstants } from "../../constants/ApiConstants";
import { useDispatch } from "react-redux";
import { memberActions } from "../../redux/slices/memberSlice";
import { loyaltyPointsActions } from "../.././redux/slices/UpdateLoyaltyPoints";
import { constant } from "../../constants";
import useGetMerchants from "../../Hooks/merchant/useGetMerchants";
import { usePermissionGetter } from "../../Hooks/Permissions/userPermissionGetter";
import { CommonButton } from "../../Shared/Components/Buttons/CommonButton";

export interface TableColumn {
  key: string;
  header: string;
  searchable?: boolean;
  filterable?: boolean;
}

export interface MemberListData {
  name: string;
  email: string;
  mobile: string;
  loyaltyNo: number;
  member_type: string;
  tier: string;
  joinDate: string;
  customer_status: string;
  loyalty_points: string;
}

const ViewMember = () => {
  const { fetchMerchantList } = useGetMerchants();
  const { getUserPermission: getMemberPermission } = usePermissionGetter(
    "members",
    "members_search"
  );
  const { getUserPermission: getCorporatePermission } = usePermissionGetter(
    "members",
    "corporate_member_logs"
  );
  const tier: String = "Silver";
  const buttonStyles = useButtonStyles();
  const [searchState, setSearchState] = useState<any>({ query: "" });
  const [scanQr, setScanQr] = useState<boolean>(false);
  const [scanQrError, setScanQrError] = useState<boolean>(false);
  const [decodeQrString, setDecodeQrString] = useState<string>("");
  const [loader, setLoader] = useState<boolean>(false);

  const userRoleGroup = useAppSelector(
    (state: any) => state.persistReducers.login.userTypeGroup
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getMemberDetails, loading } = useGetMemberDetails(searchState?.query);
  const handleScanQrToggle = () => {
    setScanQr((prevScanQr) => !prevScanQr);
  };
  useEffect(() => {
    if (searchState.query) {
      getMemberDetails(searchState?.query);
    }
  }, [searchState]);

  useEffect(() => {
    if (loading) {
      setScanQr(false);
    }
  }, [loading]);

  const displayReports = () => {
    return scanQr && <QRScanner />;
  };

  const requestBody = {
    text: decodeQrString,
    type: "customer",
  };
  const getCustomer = async () => {
    try {
      setLoader(true);
      const response = await axios.post(
        process.env.REACT_APP_API_URL + APIConstants.qrValidate,
        requestBody
      );
      const mappedData = response.data.data;
    

      if (window.location.pathname.includes("memberRedemption")) {
        navigate(`/memberRedemption`);
      } else if (window.location.pathname.includes("sales")) {
        navigate(`/sales`);
      } else {
        navigate(`/viewMember`);
      }
      dispatch(
        memberActions.updateMemberList({
          membersList: mappedData,
        })
      );
      dispatch(
        loyaltyPointsActions.updatePoints({
          points: mappedData.loyaltyPoints,
        })
      );
      setScanQrError(false);
    } catch (e) {
      setScanQrError(true);
    } finally {
      let input = document.getElementById("qrStringInput");
      if (input) (input as HTMLInputElement).value = "";
      setLoader(false);
    }
  };

  useEffect(() => {
    if (decodeQrString) getCustomer();
  }, [decodeQrString]);

  const displayMemberSearch = () => {
    if (window.location.pathname === "/cs-admin/search/sales") {
      displayReports();
      return;
    }
    if (!loading) return scanQr ? <QRScanner /> : null;
    else
      return (
        <Grid
          item
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="300px"
          width={"100%"}
        >
          <Loader />
        </Grid>
      );
  };
  const deb = debounce((value: string) => {
    setDecodeQrString(value);
  }, 500);
  const processQrString = (qrString: string) => {
    deb(qrString);
  };

  const displayMemberHeader = () => {
    return (
      <Grid
        display={"flex"}
        flexDirection={"column"}
        height={"100%"}
        borderRadius={4}
        sx={{
          background: theme.color?.background,
          backgroundImage: `url(${backgroundImageToLogin})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
        }}
        flex={12}
      >
        <Grid
          display={"flex"}
          gap={3}
          justifyContent={"end"}
          alignItems={"center"}
          px={4}
          py={2}
          flex={2}
        >
          {getMemberPermission("create") && (
            <Link to={"/addMember"}>
              <CommonButton
                buttonTitle="Add Member"
                variant="outlined"
                color="secondary"
              />
            </Link>
          )}
          {getCorporatePermission("read") && (
            <Link to={"/corporate"}>
              <CommonButton
                buttonTitle="Corporate Member Logs"
                variant="outlined"
                color="secondary"
              />
            </Link>
          )}
        </Grid>
        <Grid
          flex={10}
          textAlign={"center"}
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          pt={4}
          gap={1}
        >
          {getMemberPermission("read") && (
            <Typography
              sx={{
                fontFamily: theme.font?.fontFamily.primary,
                fontSize: "32px",
              }}
            >
              Search Member
            </Typography>
          )}
          {getMemberPermission("read") && (
            <Grid
              display={"flex"}
              justifyContent={"center"}
              gap={4}
              alignItems={"center"}
              width={"60%"}
              sx={{
                borderRadius: "16px",
                background: theme.color?.searchBackground,
              }}
              px={2}
              py={2}
            >
              <Grid width={"436px"} gap={4}>
                <SearchMemberDropdown
                  options={[]}
                  setState={setSearchState}
                  secondaryStateKey="query"
                  stateKey="query"
                />
              </Grid>
              <Typography
                sx={{
                  color: theme.color?.newFilterGrey,
                  fontFamily: theme?.font?.fontFamily?.primary,
                }}
              >
                or
              </Typography>
              <Grid item display={"flex"}>
                <CommonButton
                  variant="contained"
                  color="secondary"
                  buttonTitle="Scan QR Code"
                  onClick={handleScanQrToggle}
                />
              </Grid>
            </Grid>
          )}
          {scanQr && (
            <>
              <BasicModal
                disableAutoFocus={true}
                open={scanQr}
                toggleModal={setScanQr}
                content={
                  scanQrError ? (
                    <ScannerModalFail
                      closeDrawer={setScanQr}
                      resetScanner={setScanQrError}
                    />
                  ) : (
                    <ScannerModal
                      closeDrawer={setScanQr}
                      resetScanner={setScanQrError}
                    />
                  )
                }
                title="please scan"
              ></BasicModal>
              <TextField
                autoFocus
                onChange={(event) => processQrString(event?.target?.value)}
                id="qrStringInput"
                onBlur={(e) => e.target.focus()}
                autoComplete="off"
              ></TextField>
            </>
          )}
          {/* {scanQr && <QRScanner />} */}
        </Grid>
        {loading && <BasicModal content={<Loader />} />}
      </Grid>
    );
  };

  return (
    <Grid container justifyContent={"center"} height={"100%"}>
      {displayMemberHeader()}
      {loader && <BasicModal content={<Loader />} />}
    </Grid>
  );
};

export default ViewMember;
